import { useEffect, useState } from "react"
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Badge,
  HStack,
  Stack,
  Code,
  Button,
} from "@chakra-ui/react"
import axios from "axios"

const formatDateTime = (iso) => {
  const d = new Date(iso)
  return d.toLocaleString()
}

const LogViewerScreen = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [pagination, setPagination] = useState(null)
  const [cursorStack, setCursorStack] = useState([])
  const [detailsMap, setDetailsMap] = useState({})
  const [detailsLoadingId, setDetailsLoadingId] = useState(null)
  const [detailsError, setDetailsError] = useState(null)
  

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true)
        const cursor = cursorStack[page - 2] || null
        const includeCount = page === 1
        const params = new URLSearchParams()
        params.set("limit", String(pageSize))
        if (!includeCount) params.set("includeCount", "false")
        params.set("includeDetails", "false")
        if (cursor) {
          params.set("cursorCreatedAt", cursor.createdAt)
          params.set("cursorId", cursor.id)
        }
        const { data } = await axios.get(`/api/logs?${params.toString()}`)
        setLogs(data.items || [])
        setPagination(data.pagination || null)
      } catch (err) {
        setError(err.response?.data || err.message || "Failed to load logs")
      } finally {
        setLoading(false)
      }
    }

    fetchLogs()
  }, [page, pageSize])

  const handlePrev = () => {
    if (page <= 1) return
    const newPage = page - 1
    setPage(newPage)
  }

  const handleFirst = () => {
    setCursorStack([])
    setPage(1)
  }
  const loadDetails = async (id) => {
    try {
      setDetailsError(null)
      setDetailsLoadingId(id)
      const { data } = await axios.get(`/api/logs/${id}`)
      setDetailsMap((prev) => ({ ...prev, [id]: data.details }))
    } catch (err) {
      setDetailsError(err.response?.data || err.message || "Failed to load details")
    } finally {
      setDetailsLoadingId(null)
    }
  }

  const handleNext = () => {
    if (!pagination?.hasMore) return
    const last = logs[logs.length - 1]
    if (!last) return
    const nextCursor = { createdAt: last.createdAt, id: last._id }
    setCursorStack((prev) => {
      const copy = [...prev]
      copy[page - 1] = nextCursor
      return copy
    })
    setPage(page + 1)
  }


  return (
    <Box maxW="6xl" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <HStack mb={4} justify="space-between" align="center">
        <HStack spacing={4} align="center">
          <Heading size="lg">Activity Logs</Heading>
          {pagination && (
            <Badge colorScheme="cyan">
              Page {page}{pagination.totalCount ? ` (${pagination.totalCount} total)` : ""}
            </Badge>
          )}
        </HStack>
        {pagination && (
          <HStack spacing={3}>
            <Badge colorScheme="gray">
              Showing {logs.length}{pagination.totalCount ? ` of ${pagination.totalCount}` : ""}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={handleFirst}
              isDisabled={page === 1}
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              isDisabled={page === 1}
              onClick={handlePrev}
            >
              Prev
            </Button>
            <Text>Page {page}</Text>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!pagination.hasMore}
              onClick={handleNext}
            >
              Next
            </Button>
          </HStack>
        )}
      </HStack>

      {loading && (
        <HStack>
          <Spinner size="md" />
          <Text>Loading logs…</Text>
        </HStack>
      )}

      {error && <Text color="red.500">{error}</Text>}

      {!loading && !error && (
        <Box borderWidth="1px" borderRadius="lg" overflowX="auto">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>When</Th>
                <Th>Initials</Th>
                <Th>Action</Th>
                <Th>User</Th>
                <Th>Details</Th>
              </Tr>
            </Thead>
            <Tbody>
              {logs.map((log) => (
                <Tr key={log._id}>
                  <Td whiteSpace="nowrap">{formatDateTime(log.createdAt)}</Td>
                  <Td>{log.initials}</Td>
                  <Td>{log.actionType}</Td>
                  <Td>{log.userEmail || ""}</Td>
                  <Td maxW="420px">
                    {detailsMap[log._id] ? (
                      <Code whiteSpace="pre-wrap" fontSize="xs">
                        {JSON.stringify(detailsMap[log._id])}
                      </Code>
                    ) : (
                      <HStack>
                        <Button
                          size="xs"
                          variant="outline"
                          isLoading={detailsLoadingId === log._id}
                          onClick={() => loadDetails(log._id)}
                        >
                          View
                        </Button>
                        {detailsError && detailsLoadingId === null && (
                          <Text fontSize="xs" color="red.500">{detailsError}</Text>
                        )}
                      </HStack>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination controls moved to top */}
    </Box>
  )
}

export default LogViewerScreen
